'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTagadaContext } from '../providers/TagadaProvider';
import { TagadaError, TagadaErrorCode } from '../../core/errors';

export interface GeoLocationData {
    // Basic IP and location data
    ip_address?: string | null;
    city?: string | null;
    city_geoname_id?: number | null;
    region?: string | null;
    region_iso_code?: string | null;
    region_geoname_id?: number | null;
    postal_code?: string | null;
    country?: string | null;
    country_code?: string | null;
    country_geoname_id?: number | null;
    country_is_eu?: boolean | null;
    continent?: string | null;
    continent_code?: string | null;
    continent_geoname_id?: number | null;
    latitude?: number | null;
    longitude?: number | null;

    // Security information
    security?: {
        is_vpn?: boolean | null;
    } | null;

    // Timezone information
    timezone?: {
        name?: string | null;
        abbreviation?: string | null;
        gmt_offset?: number | null;
        current_time?: string | null;
        is_dst?: boolean | null;
    } | null;

    // Currency information
    currency?: {
        currency_name?: string | null;
        currency_code?: string | null;
    } | null;

    // Connection information
    connection?: {
        autonomous_system_number?: number | null;
        autonomous_system_organization?: string | null;
        connection_type?: string | null;
        isp_name?: string | null;
        organization_name?: string | null;
    } | null;

    // Flag information
    flag?: {
        emoji?: string | null;
        unicode?: string | null;
        png?: string | null;
        svg?: string | null;
    } | null;

    // Legacy fields for backward compatibility
    as?: string | null;
    isp?: string | null;
    lat?: number | null;
    lon?: number | null;
    org?: string | null;
    query?: string | null;
    regionName?: string | null;
    status?: string | null;
    zip?: string | null;

    // Error handling
    error?: string;
}

export interface UseGeoLocationOptions {
    /**
     * Whether to automatically fetch geolocation data on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Custom IP address to fetch geolocation for
     * If not provided, will use the client's IP
     */
    ip?: string;

    /**
     * Whether to refetch data when the hook mounts
     * @default false
     */
    refetchOnMount?: boolean;
}

export interface UseGeoLocationReturn {
    /**
     * The geolocation data
     */
    data: GeoLocationData | null;

    /**
     * Whether the request is currently loading
     */
    isLoading: boolean;

    /**
     * Any error that occurred during the request
     */
    error: TagadaError | null;

    /**
     * Function to manually fetch geolocation data
     */
    fetchGeoData: (ip?: string) => Promise<void>;

    /**
     * Function to clear the current data and error state
     */
    clearData: () => void;

    /**
     * Whether the data is from localhost/development
     */
    isLocalhost: boolean;

    /**
     * Whether the current IP is valid
     */
    isValidIP: boolean;
}

/**
 * Hook to fetch and manage geolocation data - V2 Implementation
 * Compatible with V1 interface while using V2 provider architecture
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, isLoading, error, fetchGeoData } = useGeoLocation();
 *   
 *   if (isLoading) return <div>Loading location...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   
 *   return (
 *     <div>
 *       <p>Country: {data?.country}</p>
 *       <p>City: {data?.city}</p>
 *       <p>IP: {data?.ip_address}</p>
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { fetchGeoData } = useGeoLocation({ autoFetch: false });
 *   
 *   const handleClick = () => {
 *     fetchGeoData('8.8.8.8'); // Fetch for specific IP
 *   };
 *   
 *   return <button onClick={handleClick}>Get Location for Google DNS</button>;
 * }
 * ```
 */
export function useGeoLocation(options: UseGeoLocationOptions = {}): UseGeoLocationReturn {
    const { autoFetch = true, ip, refetchOnMount = false } = options;
    const { apiService } = useTagadaContext();

    const [data, setData] = useState<GeoLocationData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Helper function to check if IP is localhost
    const isLocalhost = useCallback((ipAddress?: string | null): boolean => {
        if (!ipAddress) return false;
        return ipAddress === '127.0.0.1' || ipAddress === '::1' || ipAddress === 'localhost';
    }, []);

    // Helper function to validate IP address format
    const isValidIP = useCallback((ipAddress?: string | null): boolean => {
        if (!ipAddress) return false;

        // IPv4 regex pattern
        const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        // IPv6 regex pattern (simplified)
        const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;

        if (!ipv4Pattern.test(ipAddress) && !ipv6Pattern.test(ipAddress)) {
            return false;
        }

        // Additional validation for IPv4
        if (ipv4Pattern.test(ipAddress)) {
            const parts = ipAddress.split('.');
            return parts.every((part) => {
                const num = parseInt(part, 10);
                return num >= 0 && num <= 255;
            });
        }

        return true;
    }, []);

    // Function to fetch geolocation data
    const fetchGeoData = useCallback(async (customIp?: string) => {
        if (!apiService) {
            setError('API service not available');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const targetIp = customIp || ip;
            const endpoint = targetIp ? `/api/v1/geo?ip=${encodeURIComponent(targetIp)}` : '/api/v1/geo';


            const geoData = await apiService.fetch<GeoLocationData>(endpoint, {
                method: 'GET',
                skipAuth: true, // Geolocation endpoint doesn't require authentication
            });

            setData(geoData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch geolocation data';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [apiService, ip]);

    // Function to clear data and error state
    const clearData = useCallback(() => {
        setData(null);
        setError(null);
    }, []);

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch && apiService) {
            void fetchGeoData();
        }
    }, [autoFetch, apiService, fetchGeoData]);

    // Refetch on mount if enabled
    useEffect(() => {
        if (refetchOnMount && apiService) {
            void fetchGeoData();
        }
    }, [refetchOnMount, apiService, fetchGeoData]);

    const tagadaError = useMemo(
        () => error ? new TagadaError(error, { code: TagadaErrorCode.NETWORK_ERROR }) : null,
        [error],
    );

    return {
        data,
        isLoading,
        error: tagadaError,
        fetchGeoData,
        clearData,
        isLocalhost: isLocalhost(data?.ip_address),
        isValidIP: isValidIP(data?.ip_address),
    };
}
